import { createSelector } from 'reselect';
import {
  getScenariosKeptForever,
  dispatchersReducer,
  jobsReducer,
  paramsReducer,
  regressionPageReducer,
  scenarioReducer
} from '../reducers';
import { keys, toLower } from 'lodash';
import {
  auth,
  actionTypes as actionConstants,
  annotation as annotationConstants,
  date,
  params as paramConstants,
  regressionPages as regressionPageHelpers,
  utils,
  params as paramHelpers
} from '../helpers';
import * as actionItemHelpers from '../helpers/actionItem';
import { modal as modalActions } from '../actions';
import { getAnnotationsByScenarioIdsOrderByDate } from './annotation';
import ActionButtonListComponent from '../Components/ActionButtonListComponent';

/**
 * Retrieves an array actions to show under a dropdown
 * menu for a dispatcher context
 */
export const getDispatcherActions = createSelector(
  paramsReducer.paramsByJobIds,
  (_state, dispatcherJobInfo) => dispatcherJobInfo,
  (_state, _dispatcherJobInfo, requestedActions) => requestedActions,
  (paramsByJobIds, jobInfo, requestedActions) => {
    const dispatcherParams = paramsByJobIds && paramsByJobIds[jobInfo.jobId];
    if (!(dispatcherParams && requestedActions) ) {
      return [];
    }

    const availableActionsByName = {
      [actionConstants.OPEN_JOB_URL] : [{
        title: actionConstants.OPEN_JOB_URL,
        onSelect: () => actionItemHelpers._openJobUrl(
          dispatcherParams[paramConstants.JENKINS_BUILD_URL]
        ),
        isDisabled: !jobInfo.jobId
      }]
    };

    const result = requestedActions
      .map(name => availableActionsByName[name])
      .filter(a => a && a[0]);
    return result;
  }
);


/**
 * Retrieves an array actions to show under a dropdown
 * menu for a single Regression scenario context
 */
export const getRegressionActions = createSelector(
  getAnnotationsByScenarioIdsOrderByDate,
  paramsReducer.paramsByJobIds,
  getScenariosKeptForever,
  (state, scenario) => jobsReducer.getJobById(state, scenario.runner),
  (state, scenario) =>
    scenarioReducer.getScenariosByDispatcherId(state, scenario.dispatcher),
  //(state, scenario) => dispatchersReducer.getDispatcherById(state, scenario.dispatcher),
  dispatchersReducer.dispatchersById,
  (_state, scenario) => scenario,
  (_state, _scenario, requestedActions) => requestedActions,
  (_state, _scenario, _actionNames, position) => position,
  (_state, _scenario, _actionName, _position, title) => title,
  (
    annotationsByIds,
    paramsByJobIds,
    scenariosKeptForever,
    runnerJob,
    siblingScenarios,
    dispatchersById,
    scenario,
    requestedActions,
    position,
    title
  ) => {
    const annotation = annotationsByIds && scenario && annotationsByIds[scenario.id] ?
      annotationsByIds[scenario.id][0] : null;
    const runner = scenario ? scenario.runner : null;
    const params = runner && paramsByJobIds ? paramsByJobIds[runner] : {};

    // wait for recalculation with all expected data
    if (params === undefined || params === null || !scenario) {
      return [];
    }

    console.log(scenario);
    console.log(dispatchersById);
    console.log(dispatchersById[scenario.dispatcher]);


    const dispatcher = scenario && dispatchersById[scenario.dispatcher];
    const dispatcherParams = dispatcher && paramsByJobIds[dispatcher.job_id] || {};
    console.log(dispatcherParams);
    const scenarioId = scenario.id;
    let server = '';

    // job names might be under folders, if still on prem then we need backslashes for path building
    let jobName = params[paramConstants.JENKINS_JOB_NAME] || '';
    if (!utils.stringToBool(dispatcherParams[paramConstants.JENKINS_USE_S3]))
    {
      jobName = jobName.replace('/', '\\');
    }
    const jobNumber = params[paramConstants.JENKINS_BUILD_NUMBER] || '';

    const hyphenIndex = title.lastIndexOf('-');
    if (keys(params).length > 0 && hyphenIndex !== -1) {
      if (params[paramConstants.JENKINS_BUILD_URL])
        server = params[paramConstants.JENKINS_BUILD_URL].substring(
          params[paramConstants.JENKINS_BUILD_URL].indexOf('//') + 2,
          params[paramConstants.JENKINS_BUILD_URL].indexOf('/', 8));
    }

    const diffTypes = params[paramConstants.DIFFTYPES] ?
      params[paramConstants.DIFFTYPES].split(', ') : [];

    const hasEnoughJenkinsInfo = actionItemHelpers.hasEnoughJenkinsInfo(
      params[paramConstants.JENKINS_BOT_SERVER_NAME],
      jobName,
      jobNumber
    );

    const availableActionsByName = {
      [actionConstants.BREAK]: [{ isSeparator: true }],
      [annotationConstants.ALL_ANNOTATIONS_STATUSES.UNDER_INVESTIGATION]: [{
        title: annotationConstants.ALL_ANNOTATIONS_STATUSES.UNDER_INVESTIGATION,
        onSelect: modalActions.showChangeCommenterName(
          true, scenarioId,
          annotationConstants.ALL_ANNOTATIONS_STATUSES.UNDER_INVESTIGATION, ''),
        isDisabled: annotation && annotation.status ===
          annotationConstants.ALL_ANNOTATIONS_STATUSES.UNDER_INVESTIGATION
      }],
      [annotationConstants.ALL_ANNOTATIONS_STATUSES.NEEDS_INVESTIGATION]: [{
        title: annotationConstants.ALL_ANNOTATIONS_STATUSES.NEEDS_INVESTIGATION,
        onSelect: modalActions.showChangeCommenterName(
          true, scenarioId,
          annotationConstants.ALL_ANNOTATIONS_STATUSES.NEEDS_INVESTIGATION, ''),
        isDisabled: !annotation || annotation.status ===
          annotationConstants.ALL_ANNOTATIONS_STATUSES.NEEDS_INVESTIGATION
      }],
      [annotationConstants.ALL_ANNOTATIONS_STATUSES.NO_ACTION_REQUIRED]: [{
        title: annotationConstants.ALL_ANNOTATIONS_STATUSES.NO_ACTION_REQUIRED,
        onSelect: modalActions.showChangeCommenterName(
          true, scenarioId,
          annotationConstants.ALL_ANNOTATIONS_STATUSES.NO_ACTION_REQUIRED, ''),
        isDisabled: annotation && annotation.status ===
          annotationConstants.ALL_ANNOTATIONS_STATUSES.NO_ACTION_REQUIRED
      }],
      [actionConstants.ADD_COMMENT]: [{
        title: actionConstants.ADD_COMMENT,
        onSelect: modalActions
          .showAddComment(true, scenario, 0, '', position),
        isDisabled: !auth.checkAuthenticated()
      }],
      [actionConstants.SHOW_COMMENTS]: [{
        title: actionConstants.SHOW_COMMENTS,
        onSelect: modalActions
          .showCommentHistory(true, scenario),
        isDisabled: annotation === null
      }],
      [actionConstants.OPEN_JOB_URL]: [{
        title: actionConstants.OPEN_JOB_URL,
        onSelect: actionItemHelpers._openScenarioJobUrl(
          runnerJob,
          paramsByJobIds,
          siblingScenarios
        )
      }],
      [actionConstants.COPY_DIFF_RESULT_PATH]: [{
        title: actionConstants.COPY_DIFF_RESULT_PATH,
        onSelect: actionItemHelpers._copyTextFilePath(
          params[paramConstants.JENKINS_DIFF_OUTPUT_PATH]),
        isDisabled: !params[paramConstants.JENKINS_DIFF_OUTPUT_PATH]
      }],
      [actionConstants.COPY_LOG_FILE_PATH]: [{
        title: actionConstants.COPY_LOG_FILE_PATH,
        onSelect: actionItemHelpers._copyTextFilePath(
          params[paramConstants.JENKINS_OUTPUT_PATH]),
        isDisabled: !params[paramConstants.JENKINS_OUTPUT_PATH]
      }],
      [actionConstants.COPY_FILE_PATH]: [{
        title: actionConstants.COPY_FILE_PATH,
        onSelect: actionItemHelpers._copyFilePath(
          toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
          jobName,
          jobNumber,
          dispatcherParams
        ),
        isDisabled: hyphenIndex === -1 || !hasEnoughJenkinsInfo
      }],
      [actionConstants.OPEN_S3_PATH]: [{
        title: actionConstants.OPEN_S3_PATH,
        onSelect: actionItemHelpers._openS3Path(
          toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
          jobName,
          jobNumber
        ),
        isDisabled: hyphenIndex === -1 || !hasEnoughJenkinsInfo ||
            !utils.stringToBool(dispatcherParams[paramHelpers.JENKINS_USE_S3])
      }],
      [actionConstants.COPY_TREE_FILE_PATH]: [{
        title: actionConstants.COPY_TREE_FILE_PATH,
        onSelect: actionItemHelpers._copyTreeFilePath(
          toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
          jobName,
          jobNumber),
        isDisabled: hyphenIndex === -1 || !hasEnoughJenkinsInfo
      }],
      [actionConstants.COPY_FILE_PATH_EXPORT_TESTS]: [{
        title: actionConstants.COPY_FILE_PATH_EXPORT_TESTS,
        onSelect: actionItemHelpers._copyFilePath(
          toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
          jobName,
          jobNumber,
          dispatcherParams
        ),
        isDisabled: hyphenIndex === -1 || !hasEnoughJenkinsInfo
      }],
      [actionConstants.RETRIGGER_SCENARIO]: [{
        title: actionConstants.RETRIGGER_SCENARIO,
        onSelect: actionItemHelpers._startJob(runner)
      }],
      [actionConstants.SET_AS_BASELINE]: [{
        title: actionConstants.SET_AS_BASELINE,
        onSelect: modalActions.
          showAcknowledgeBaselineUpdateModal(true, scenario, title)
      }],
      [actionConstants.KEEP_FOREVER]: [{
        title: actionConstants.KEEP_FOREVER,
        onSelect: actionItemHelpers._keepBuildForever(jobName, server,
          jobNumber, scenariosKeptForever),
        isDisabled: !hasEnoughJenkinsInfo
      }],
      [actionConstants.DONT_KEEP_FOREVER]: [{
        title: actionConstants.DONT_KEEP_FOREVER,
        onSelect:
          actionItemHelpers._dontKeepBuildForever(jobName, server,
            jobNumber, scenariosKeptForever),
        isDisabled: !hasEnoughJenkinsInfo
      }],
      [actionConstants.SHOW_HISTORY]: [{
        title: actionConstants.SHOW_HISTORY,
        onSelect: modalActions.
          showHistory(true, scenario.name)
      }],
      [actionConstants.VIEW_TEST_ARTIFACTS]: [{
        title: actionConstants.VIEW_TEST_ARTIFACTS,
        onSelect: modalActions
          .showTestArtifactsModal(true, scenario.runner)
      }],
      [actionConstants.OPEN_DIFF_VIEWER]: [
        {
          title: actionConstants.OPEN_DIFF_VIEWER_C2C,
          onSelect: actionItemHelpers._openDiffViewer(
            'c2c',
            toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
            jobName,
            jobNumber,
            dispatcherParams
          ),
          isDisabled: (!diffTypes.includes('c2c')) || !hasEnoughJenkinsInfo
        },
        {
          title: actionConstants.OPEN_DIFF_VIEWER_N2N,
          onSelect: actionItemHelpers._openDiffViewer(
            'n2n',
            toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
            jobName,
            jobNumber,
            dispatcherParams
          ),
          isDisabled: !(diffTypes.includes('n2n')) || !hasEnoughJenkinsInfo
        }
      ]
    };

    const result = requestedActions
      .map(name => availableActionsByName[name])
      .filter(a => a && a[0]);
    return result;
  }
);


/**
 * A generic method to help build out a table for displaying Scenario based content.
 * * This method in particular generates a sorted array of column descriptor objects.
 * * Each object will hold attributes and transformation functions to help data get populated
 * * into each respective column
 * Each column object:
 *  {
 *    headerTitle {str} -> the title given to the column
 *    hideHeader {bool} -> whether or not to show header title
 *    valueType {str} -> self-claimed arbitrary type describing the data found in the column
 *    valueFunc {func} -> func accepting a single object arg to produce a value for the cell,
 *    searchable {bool} -> indicates whether this column should be included in
 *      search/filter considerations
 *    colourFunc {func} -> func determines which colour to display for the cell
 *    onClick {func} -> function to run when cell is clicked. Is passed arg a generic
 *      dispatchableAction arg that is otherwise a redux action function to be dispatched
 *    tooltipFunc {func} -> generates a string to display when user hovers over cell
 * }
 */
export const getReducedHeaders = createSelector(
  regressionPageReducer.getRegressionPage,
  (regressionPageInfo) => (trendingTable) => {
    const reducedHeaders = [];

    // column centers on an icon related to the status of the latest comment on the scenario
    if (!trendingTable) {
      reducedHeaders.push(
        {
          headerTitle: 'AnnotationIcon',
          hideHeader: true,
          valueType: regressionPageHelpers.COLUMN_TYPES.ICON,
          valueFunc: ({ annotations }) => {
            return annotationConstants.getAnnotationStatusObject(annotations && annotations[0]);
          },
        }
      );
    }

    // column centers on an icon related to comment count for the scenario
    reducedHeaders.push(
      {
        headerTitle: 'CommentIcon',
        hideHeader: true,
        valueType: regressionPageHelpers.COLUMN_TYPES.ICON,
        valueFunc: ({ annotations }) => {
          const numComments = (annotations || [])
            .filter(a => a.comment || a.ticket)
            .length;

          if (numComments) {
            return {
              title: `${numComments} comment(s)`,
              glyph: 'comment'
            };
          } else {
            return null;
          }
        }
      }
    );

    // column centers on the first piece of data related to the scenario
    reducedHeaders.push(
      {
        headerTitle: regressionPageInfo.scenarioFeed.scenarioTypeLabel,
        valueType: regressionPageHelpers.COLUMN_TYPES.TEXT,
        valueFunc: regressionPageInfo.scenarioFeed.scenarioTypeFunc,
        searchable: true
      }
    );

    // column centers on the second piece of data related to the scenario
    if (regressionPageInfo.scenarioFeed.scenarioSecondaryTypeLabel) {
      reducedHeaders.push({
        headerTitle: regressionPageInfo.scenarioFeed.scenarioSecondaryTypeLabel,
        valueType: regressionPageHelpers.COLUMN_TYPES.TEXT,
        valueFunc: regressionPageInfo.scenarioFeed.scenarioSecondaryTypeFunc,
        searchable: true
      });
    }

    // column center on the name of the scenario
    if (regressionPageInfo.scenarioFeed.scenarioNameLabel) {
      reducedHeaders.push({
        headerTitle: regressionPageInfo.scenarioFeed.scenarioNameLabel,
        valueType: regressionPageHelpers.COLUMN_TYPES.TEXT,
        valueFunc: regressionPageInfo.scenarioFeed.scenarioNameFunc,
        searchable: true
      });
    }


    if (!trendingTable) {
      reducedHeaders.push(
        {
          headerTitle: 'Dispatched',
          valueType: regressionPageHelpers.COLUMN_TYPES.TEXT,
          valueFunc: ({ scenario }) => date.friendlyDate(scenario.created_at)
        },
        {
          headerTitle: 'Last Updated',
          valueType: regressionPageHelpers.COLUMN_TYPES.TEXT,
          valueFunc: ({ scenario }) => date.friendlyDate(scenario.updated_at)
        },
        {
          headerTitle: 'Actions',
          valueType: regressionPageHelpers.COLUMN_TYPES.ACTIONABLE,
          valueFunc: () => ActionButtonListComponent
        }
      );
    } else {
      reducedHeaders.push(
        ...regressionPageHelpers.trendingTableRecentRunHeaders(regressionPageInfo)
      );
    }

    return reducedHeaders;
  }
);


// WEBPACK FOOTER //
// src/selectors/regressionPage.js